/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {reInitializeState} from '@ion/applications/src/helpers';
import {MaintenanceSchedule} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageState} from '@/types/pages';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_MAINTENANCE_SCHEDULE,
  ADD_ADMIN_MAINTENANCE_SCHEDULE_ERROR,
  ADD_ADMIN_MAINTENANCE_SCHEDULE_REQUEST,
  ADD_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE,
  ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED,
  DELETE_ADMIN_MAINTENANCE_SCHEDULE,
  DELETE_ADMIN_MAINTENANCE_SCHEDULE_ERROR,
  DELETE_ADMIN_MAINTENANCE_SCHEDULE_REQUEST,
  DELETE_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE,
  EDIT_ADMIN_MAINTENANCE_SCHEDULE,
  EDIT_ADMIN_MAINTENANCE_SCHEDULE_ERROR,
  EDIT_ADMIN_MAINTENANCE_SCHEDULE_REQUEST,
  EDIT_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE,
  GET_ADMIN_MAINTENANCE_SCHEDULES_ERROR,
  GET_ADMIN_MAINTENANCE_SCHEDULES_REQUEST,
  GET_ADMIN_MAINTENANCE_SCHEDULES_RESPONSE,
  INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES,
  RE_INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES,
  RESET_DELETE_ADMIN_MAINTENANCE_SCHEDULE,
  SET_ADMIN_MAINTENANCE_SCHEDULES_GRID_FILTER_MODEL,
  SET_GRID_COLUMNS,
  UPDATE_GRID_COLUMNS
} from '../actions/admin-maintenance-schedules';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '../helpers';

export interface AdminMaintenanceScheduleAction extends AdminAction {
  schedule?: MaintenanceSchedule;
  schedules?: MaintenanceSchedule[];
};

interface AdminMaintenanceSchedulesActionState extends AsyncState {
  selected: MaintenanceSchedule | MaintenanceSchedule[] | null;
  result: MaintenanceSchedule | MaintenanceSchedule[] | null;
  isEditing: boolean;
};

export interface AdminMaintenanceSchedulesState extends PageState {
  schedules: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    filterModel: FilterModel | {}
  },
  add: AdminMaintenanceSchedulesActionState,
  edit: AdminMaintenanceSchedulesActionState,
  destroy: AdminMaintenanceSchedulesActionState,
}

export interface AdminMaintenanceSchedulesAction extends AdminAction {
  schedule?: MaintenanceSchedule;
  schedules?: MaintenanceSchedule[];
};

// defines the user preference key for the admin maintenance schedules page ...
const PREFERENCE = 'admin-maintenance-schedules';

// defines the initial state for the reducer ...
export const initialState: AdminMaintenanceSchedulesState = {
  isInitialized : false,
  schedules     : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    filterModel         : {}
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  }
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;
  },
  [RE_INITIALIZE_ADMIN_MAINTENANCE_SCHEDULES] : draft => {
    reInitializeState(draft, {initialState});
  },
  [SET_GRID_COLUMNS] : ({grid, schedules}: AdminMaintenanceSchedulesState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : schedules.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: AdminMaintenanceSchedulesState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_MAINTENANCE_SCHEDULES_GRID_REFRESHED] : (
    {add, edit, destroy}: AdminMaintenanceSchedulesState) => {
    add.result = null;
    edit.result = null;
    destroy.result = null;
  },
  [GET_ADMIN_MAINTENANCE_SCHEDULES_REQUEST] : ({schedules}: AdminMaintenanceSchedulesState) => {
    schedules.loading = true;
  },
  [GET_ADMIN_MAINTENANCE_SCHEDULES_RESPONSE] : ({schedules}: AdminMaintenanceSchedulesState) => {
    schedules.loading = false;
  },
  [GET_ADMIN_MAINTENANCE_SCHEDULES_ERROR] : (
    {schedules}: AdminMaintenanceSchedulesState, {error}: AdminMaintenanceSchedulesAction
  ) => {
    schedules.loading = false;
    schedules.error   = error;
  },

  [ADD_ADMIN_MAINTENANCE_SCHEDULE] : (
    {add}: AdminMaintenanceSchedulesState, {show}: AdminMaintenanceScheduleAction
  ) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_ADMIN_MAINTENANCE_SCHEDULE_REQUEST] : ({add}: AdminMaintenanceSchedulesState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE] : (
    {add}: AdminMaintenanceSchedulesState, {schedule}: AdminMaintenanceScheduleAction
  ) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = schedule;
  },
  [ADD_ADMIN_MAINTENANCE_SCHEDULE_ERROR] : (
    {add}: AdminMaintenanceSchedulesState, {error}: AdminMaintenanceScheduleAction
  ) => {
    add.loading   = false;
    add.error     = error;
  },

  [EDIT_ADMIN_MAINTENANCE_SCHEDULE] : (
    {edit}: AdminMaintenanceSchedulesState, {schedule, show}: AdminMaintenanceScheduleAction
  ) => {
    edit.selected  = show ? schedule : null;
    edit.isEditing = show;
    edit.result    = null;
  },
  [EDIT_ADMIN_MAINTENANCE_SCHEDULE_REQUEST] : ({edit}: AdminMaintenanceSchedulesState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE] : (
    {edit}: AdminMaintenanceSchedulesState, {schedule}: AdminMaintenanceScheduleAction
  ) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = schedule;
  },
  [EDIT_ADMIN_MAINTENANCE_SCHEDULE_ERROR] : (
    {edit}: AdminMaintenanceSchedulesState, {error}: AdminMaintenanceScheduleAction
  ) => {
    edit.loading   = false;
    edit.error     = error;
  },

  [DELETE_ADMIN_MAINTENANCE_SCHEDULE] : (
    {destroy}: AdminMaintenanceSchedulesState, {schedule, show}: AdminMaintenanceSchedulesAction
  ) => {
    destroy.selected  = show ? schedule : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [RESET_DELETE_ADMIN_MAINTENANCE_SCHEDULE] : ({destroy}: AdminMaintenanceSchedulesState) => {
    destroy.result    = null;
  },
  [DELETE_ADMIN_MAINTENANCE_SCHEDULE_REQUEST] : ({destroy}: AdminMaintenanceSchedulesState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_ADMIN_MAINTENANCE_SCHEDULE_RESPONSE] : (
    {destroy}: AdminMaintenanceSchedulesState, {schedule}: AdminMaintenanceSchedulesAction
  ) => {
    destroy.loading    = false;
    destroy.isEditing  = false;
    destroy.result     = schedule;
  },
  [DELETE_ADMIN_MAINTENANCE_SCHEDULE_ERROR] : (
    {destroy}: AdminMaintenanceSchedulesState, {error}: AdminMaintenanceSchedulesAction) => {
    destroy.loading   = false;
    destroy.error     = error;
    destroy.result    = null;
  },
  [SET_ADMIN_MAINTENANCE_SCHEDULES_GRID_FILTER_MODEL] : ({grid}: AdminMaintenanceSchedulesState, {model}) => {
    grid.filterModel = model;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminMaintenanceSchedulesState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
